import React, { useEffect, useRef, useState } from "react";

const SPIRIT_OPTIONS = [
  { id: "A", title: "Hohes Interesse", desc: "\"Wir lieben SalesGod und Cold Outreach ist auch geil! Können wir auch sofort loslegen?\"" },
  { id: "B", title: "Sachlich und bedacht", desc: "\"Klingt spannend - ein paar Fragen noch, dann legen wir gerne los.\"" },
  { id: "C", title: "Skeptisch", desc: "\"Das Problem das ich sehe,...\"" },
  { id: "D", title: "Nichts davon", desc: "Andere Stimmung / passt keine der drei." },
];

// DISC-Profil des Entscheiders. Optionaler Style-Nudge an die KI (~20 %),
// der die Tonalität des Angebots an den Persönlichkeitstyp anpasst.
// Wenn keiner gewählt → Prompt bleibt 1:1 wie aktuell.
const DECISION_MAKER_TYPES = [
  {
    id: "DOMINANT",
    short: "D",
    title: "Dominant",
    desc: "Direkt · Ergebnis · Kontrolle",
    accent: "#E1521D",
  },
  {
    id: "INITIATIV",
    short: "I",
    title: "Initiativ",
    desc: "Begeistert · Vision · People",
    accent: "#F2C235",
  },
  {
    id: "GEWISSENHAFT",
    short: "G",
    title: "Gewissenhaft",
    desc: "Daten · Präzision · Risiken",
    accent: "#2C5F8D",
  },
  {
    id: "STETIG",
    short: "S",
    title: "Stetig",
    desc: "Sicherheit · Vertrauen · Wir-Gefühl",
    accent: "#62A934",
  },
];

/**
 * Maximum number of priority slots displayed in the configurator.
 * AI extracts up to PRIORITY_MAX dynamic items from the brain-dump; user can
 * additionally add custom entries by clicking on placeholder slots.
 */
export const PRIORITY_MAX = 6;

/**
 * Catalogue of setup packages that BonusClub offers. The salesperson picks
 * inside the configurator which of these should be visible to the prospect
 * in the generated offer (default: only "standard"). The KI generates the
 * customer-facing wording around them; price and ID stay fixed here.
 *
 *  • standard — included; the first month doubles as the setup phase.
 *  • premium  — 3.240 € flat add-on; only offered when explicitly enabled.
 */
export const SETUP_PACKAGES = [
  {
    id: "standard",
    title: "Standard Setup",
    price: 0,
    description:
      "Im ersten Monat bauen wir die komplette Outreach-Infrastruktur sauber auf, ohne separate Setup-Gebühr. Sie zahlen den Monatspreis, das erste Monat dient als saubere Setup-Phase. Wir starten nicht mit dem Versand, sondern mit der Vorarbeit, die echte Antwortquoten überhaupt erst möglich macht: stabile Domains, gewärmte Mailboxen, eine geprüfte Zielgruppen-Definition und ein erster Copywriting-Text direkt von einem unserer Senior Outreach Experten, der den Ton trifft, den Ihre Entscheider gewohnt sind. Der Hebel liegt in der Sorgfalt vor dem ersten Versand, denn jede Nachricht, die später rausgeht, lebt von dieser Vorbereitung. Genau das ist der Unterschied zwischen hektischem Mail-Versand und einem Akquise-Prozess, der planbar Termine liefert.",
    bullets: [
      "Domains und Mailboxen werden eingerichtet",
      "Komplette technische Einrichtung & Warmup-Phase",
      "1× Email-Copywriting-Text direkt von einem unserer Senior Outreach Experten",
      "Kurzer Kick-Off-Termin",
    ],
  },
  {
    id: "premium",
    title: "Premium Setup",
    price: 3240,
    description:
      "High-End Onboarding mit Zugang zu unseren neuesten Features. Statt einem erhalten Sie hier drei vollständig von einem Senior Outreach Experten persönlich verfasste Email-Texte: der direkte Hebel für signifikant höhere Reply- und Termin-Quoten. Optional zubuchbar zu jedem Paket.",
    bullets: [
      "3× Email-Copywriting-Texte direkt von einem Senior Outreach Experten (statt 1× im Standard), kalibriert auf Ihre Zielgruppe",
      "Persönliche Copywriting-Session mit einem unserer Senior Outreach Experten",
      "Digitaler Firmen-Copywriting-Clone (AI-Powered) für laufende Konsistenz auf Ihrem Stil",
      "High-End Account Setup mit den neuesten Features und Premium-Funktionen",
    ],
  },
];

export const SETUP_PACKAGE_IDS = SETUP_PACKAGES.map((p) => p.id);

/**
 * The three offer tiers, in canonical order. Used for the optional
 * "pre-select the tier the customer wants" control in the configurator.
 * The index (0/1/2) is what gets stored in config.selectedTierIndex and is
 * mapped to the generated option id server-side (generateValueBasedOffer.js).
 */
export const OFFER_TIERS = [
  { id: "foundation", title: "Foundation", desc: "Solides Outreach-Setup für die erste Kampagne" },
  { id: "plus", title: "Plus", desc: "Mehr Volumen, mehr Steuerung, mehr Begleitung" },
  { id: "premium", title: "Premium", desc: "Maximale Tiefe, Multi-Touchpoint, Senior-Begleitung" },
];

/**
 * Legacy letter-code → label map. Kept for backward compatibility so old
 * offers that still reference letters render readably and round-trip into
 * the new free-text data model when edited.
 */
const PRIORITY_LEGACY_LABELS = {
  A: "Setup-Geschwindigkeit",
  B: 'Hochwertiges "High Profile" Copywriting',
  C: "Tägliche Routine",
  D: "Klare Kommunikation",
  E: "Extrem viele Leads",
  F: "Mehrere Kampagnen gleichzeitig",
  G: "Kreative Custom Solutions",
  H: "Monatliche Besprechung",
  I: "Wöchentliche Besprechung",
  J: "Vor-Qualifizierung",
  K: "High-Conversion Landing-Page",
  L: "2 neue Outreach-Konzepte / Monat",
  M: "Maximale Betreuung",
  N: "WhatsApp Support",
  O: '"High End" Setup',
  P: "100+ Emails pro Tag",
};

const expandLegacyPriority = (s) => {
  const v = String(s || "").trim();
  if (/^[A-P]$/i.test(v)) return PRIORITY_LEGACY_LABELS[v.toUpperCase()] || v;
  return v;
};

/**
 * Curated suggestions shown in the "+ Eigenen Punkt hinzufügen"-Picker.
 * Auswahl-Mix aus den häufigsten realen Customer-Asks (Setup, Volumen,
 * Betreuung, Quality, Workflow). Bewusst kompakt, damit der Picker nicht
 * überladen wirkt — der Nutzer kann jederzeit eigenen Text tippen.
 */
const PRIORITY_PRESETS = [
  "Schneller Setup-Start",
  "Hochwertiges High-Profile Copywriting",
  "Sehr viele qualifizierte Leads",
  "Vor-qualifizierte Termine",
  "Mehrere Kampagnen parallel",
  "Konstanter, planbarer Lead-Flow",
  "Maximale Betreuung & Sparring",
  "Wöchentliche Strategie-Calls",
  "WhatsApp-Support / kurze Wege",
  "High-Conversion Landing-Page",
  "100+ E-Mails pro Tag",
  "Klare Reportings & Transparenz",
  "Kreative Custom-Solutions",
  "Premium Brand-Auftritt",
  "Tägliche Outreach-Routine",
  "Neue Outreach-Konzepte / Monat",
];

/**
 * Mirror of the value-pricing formula from
 * functions/generateValueBasedOffer.js (kept in sync).
 *
 * Used purely for the live preview in the configurator. The cloud function
 * recomputes the same numbers server-side as the authoritative source.
 */
// Standards AND absolute minimums — prices never go below these floors.
const VALUE_BASE_PRICES = [1640, 2860, 5460];
const VALUE_MIN_PRICES = [1640, 2860, 5460];
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
// Economic model (kept in sync with VALUE_PRICING.appointmentValue):
//   30% Marge × 1/3 Conversion → Profit/Termin = D × 0.10
//   Wir nehmen 25% davon → Fee = D × 0.025 (= 2,5 % vom Deal-Wert)
//   Kunde behält 75 % → 3× ROI pro Termin (No-Brainer)
// Economic model for the appointment-bonus engine.
// — customerProfitPerAppointment = dealValue × margin × (1/3 conversion)
// — ourFee per appointment        = customerProfitPerAppointment × CAPTURE_RATE
// CAPTURE_RATE is fixed at 16 % (we keep ~1/6 of customer's expected profit).
// Floor at 15 € so very small deals don't degrade to symbolic amounts.
const APPT_CAPTURE_RATE = 0.16;
const APPT_CONVERSION_RATE = 1 / 3;

export const clampMargin = (m) => clamp(Number(m) || 30, 10, 100);
export const clampLifetime = (y) => clamp(Number(y) || 3, 1, 20);

// Appointment bonus starts at this appointment. 4 = the first 3 are included.
export const BONUS_FROM_DEFAULT = 4;

export const resolveBonusFromAppointment = (cfg) => {
  const n = Number(cfg?.bonusFromAppointment);
  if (!Number.isFinite(n)) return BONUS_FROM_DEFAULT;
  return clamp(Math.round(n), 1, 30);
};

// null = use the calculated per-appointment fee. 0 is a real override.
export const resolveAppointmentPriceOverride = (cfg) => {
  const raw = cfg?.appointmentPriceOverride;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Math.round(Number(raw));
  if (!Number.isFinite(n) || n < 0 || n > 100000) return null;
  return n;
};

export const commitAppointmentPrice = (raw, engineValue) => {
  const n = parseEuroAmount(raw);
  if (n == null) {
    return { ok: false, override: null, error: "Trag einen Termin-Preis ein." };
  }
  if (n < 0 || n > 100000) {
    return {
      ok: false,
      override: null,
      error: "Der Termin-Preis liegt zwischen 0 € und 100.000 €.",
    };
  }
  return {
    ok: true,
    override: n === engineValue ? null : n,
    error: "",
  };
};

export const commitBonusFrom = (raw) => {
  const digits = String(raw ?? "").replace(/[^\d]/g, "");
  const n = parseInt(digits, 10);
  if (!Number.isFinite(n) || n < 1 || n > 30) {
    return { ok: false, value: BONUS_FROM_DEFAULT, error: "Start zwischen dem 1. und dem 30. Termin." };
  }
  return { ok: true, value: n, error: "" };
};

const computeAppointmentValue = (deal, marginPct = 30) => {
  const d = clamp(Number(deal) || 10000, 1000, 100000);
  const margin = clampMargin(marginPct) / 100;
  const customerProfitPerAppt = d * margin * APPT_CONVERSION_RATE;
  const raw = customerProfitPerAppt * APPT_CAPTURE_RATE;
  return Math.round(Math.max(15, raw) / 10) * 10;
};
// Profit per booked appointment a customer can expect, given the model.
const computeCustomerAppointmentProfit = (deal, marginPct = 30) => {
  const d = clamp(Number(deal) || 10000, 1000, 100000);
  const margin = clampMargin(marginPct) / 100;
  return Math.round(d * margin * APPT_CONVERSION_RATE);
};

/**
 * Wertschaffungs-Level (1–10) — wie viel Wert können wir für DIESEN Kunden schaffen?
 *
 * Bewusst NUR auf Basis von Verkaufswert/Jahr × Termine/Monat (= projizierter
 * Jahresumsatz, den wir dem Kunden bringen). Andere Faktoren wie Einzigartigkeit
 * oder Markt-Bedarf werden separat im Scoring erfasst und sind hier bewusst NICHT
 * eingeflochten — sonst wirkt der Score schnell „bewertend" statt „wertschaffend".
 *
 * Eingang: projizierter Jahresumsatz des Kunden mit uns
 *   = revenuePerClose × (apptsPerMonth / 3 Closes) × 12 Monate
 *   apptsPerMonth: Slider, oder Plus-Tier-Default (12) wenn auf Auto.
 */
export const computeCustomerValueScore = (cfg = {}) => {
  const d = clamp(Number(cfg.revenuePerClose) || 10000, 1000, 100000);
  const apptOverride = Number(cfg.appointmentsPerMonth) || 0;
  const appts =
    apptOverride > 0 ? clamp(apptOverride, 0, 50) : 12; // 12 = Plus-Default
  const projectedAnnual = d * (appts / 3) * 12;

  // Piecewise linear curve mapping projected annual revenue → 1..10.
  // Tuned so realistic deal sizes land in the "Stark" / "Top" range.
  let score;
  if (projectedAnnual <= 50_000) {
    score = 1 + (projectedAnnual / 50_000) * 2; // 1 → 3
  } else if (projectedAnnual <= 200_000) {
    score = 3 + ((projectedAnnual - 50_000) / 150_000) * 2; // 3 → 5
  } else if (projectedAnnual <= 600_000) {
    score = 5 + ((projectedAnnual - 200_000) / 400_000) * 2; // 5 → 7
  } else if (projectedAnnual <= 2_000_000) {
    score = 7 + ((projectedAnnual - 600_000) / 1_400_000) * 1.5; // 7 → 8.5
  } else if (projectedAnnual <= 8_000_000) {
    score = 8.5 + ((projectedAnnual - 2_000_000) / 6_000_000) * 1; // 8.5 → 9.5
  } else {
    score = 9.5 + Math.min(0.5, ((projectedAnnual - 8_000_000) / 12_000_000) * 0.5);
  }

  return Math.max(1, Math.min(10, +score.toFixed(1)));
};

export const labelForCustomerValueScore = (score) => {
  if (score >= 8.5) return { label: "Hebel-Kunde", tone: "top" };
  if (score >= 6.5) return { label: "Stark", tone: "strong" };
  if (score >= 4.5) return { label: "Solide", tone: "ok" };
  return { label: "Klein", tone: "low" };
};

/**
 * Erfolgs-Score (1–10) — Erfolgometer für die Kampagne.
 *
 * Basiert ausschließlich auf den fünf objektiven Branchen-Faktoren. Der
 * Kunden-Spirit fließt bewusst NICHT mit ein — das ist ein subjektiver
 * Bauchgefühl-Indikator und gehört nicht in einen Erfolgs-Score.
 *
 * Direction: Slider 0 = ungünstig, Slider 10 = günstig — und die finale Skala
 * folgt derselben Richtung, damit hohe Werte intuitiv "gut" bedeuten.
 *   alle Slider auf 10  ⇒ Erfolgs-Score = 10.0 (Top-Bedingungen)
 *   alle Slider auf 0   ⇒ Erfolgs-Score = 1.0  (extrem schwierig)
 *
 * Gewichtung — was hat den größten Hebel auf den Erfolg:
 *   - Cold-Outreach-Fit (28 %) → ist Cold Outreach in der Branche überhaupt
 *                                etabliert? Hartes Limit. Killer-Faktor #1.
 *   - Markt-Bedarf       (22 %) → fehlt Bedarf, hilft kein Funnel der Welt.
 *   - Online-Auftritt    (22 %) → starkes Branding zieht in unseren Cases
 *                                empirisch deutlich höhere Reply-Raten;
 *                                Trust beim ersten Touchpoint ist riesig.
 *   - Einzigartigkeit    (14 %) → austauschbar = harte Differenzierung nötig.
 *   - Blue Ocean         (14 %) → Wettbewerbsdichte.
 *   Summe = 100 %, daher all-10s → Erfolgs-Score 10.0.
 */
export const computeCampaignSuccessScore = (cfg = {}) => {
  const co = clamp(Number(cfg.coldOutreachFitScore ?? 5), 0, 10);
  const m = clamp(Number(cfg.marketNeedScore ?? 5), 0, 10);
  const u = clamp(Number(cfg.uniquenessScore ?? 5), 0, 10);
  const b = clamp(Number(cfg.blueOceanScore ?? 5), 0, 10);
  const op = clamp(Number(cfg.onlinePresenceScore ?? 5), 0, 10);

  // Erfolgs-Score 1–10: high = gute Voraussetzungen.
  const score =
    co * 0.28 +
    m * 0.22 +
    op * 0.22 +
    u * 0.14 +
    b * 0.14;

  return Math.max(1, Math.min(10, +score.toFixed(1)));
};

export const labelForCampaignSuccess = (score) => {
  // Hoher Erfolgs-Score = bessere Bedingungen.
  if (score >= 8.5) return { label: "Top-Kampagne", tone: "top" };
  if (score >= 6.5) return { label: "Sehr gut machbar", tone: "strong" };
  if (score >= 4.5) return { label: "Solide", tone: "ok" };
  if (score >= 2.5) return { label: "Schwierig", tone: "low" };
  return { label: "Sehr schwer / Riskant", tone: "low" };
};

/**
 * Expected booked appointments per month, per SalesGod tier.
 * Conservative estimates based on the Standard-Pakete email volume:
 *   Foundation: 30 mails/day × 22 wd × ~0,7 % conversion ≈ 5 Termine/Monat
 *   Plus:       2 × 60 mails/day × 22 wd × ~0,7 % ≈ 12–14 Termine/Monat
 *   Premium:    3 × 100 mails/day × 22 wd × ~0,7 % ≈ 25–30 Termine/Monat
 */
export const TIER_EXPECTED_APPOINTMENTS = [5, 12, 25];
export const TIER_LABELS = ["Foundation", "Plus", "Premium"];

/**
 * Project the customer's revenue if they ran the tier with us:
 *   appointments/month × (1 close / 3 appointments) × deal value/Jahr
 *
 * Note: deal value is annual revenue per close — so 1 close = D €/Jahr.
 * → revenue/month = (appts/month × 1/3) × D
 * → annual revenue = 12 × revenue/month
 * Conservative: this assumes steady-state from month 1 (real ramp-up
 * usually starts from month 2), so the annual figure reflects a clean
 * 12-month run.
 */
export const computeRevenueProjection = (cfg, tierIdx) => {
  const d = clamp(Number(cfg?.revenuePerClose) || 10000, 1000, 100000);
  const margin = clampMargin(cfg?.profitMargin) / 100;
  const lifetimeYears = clampLifetime(cfg?.customerLifetimeYears);
  const override = Number(cfg?.appointmentsPerMonth) || 0;
  const apptPerMonth =
    override > 0
      ? clamp(Math.round(override), 0, 50)
      : TIER_EXPECTED_APPOINTMENTS[tierIdx] || 0;
  const closesPerMonth = apptPerMonth / 3;
  const closesPerYear = closesPerMonth * 12;
  const revenuePerMonth = closesPerMonth * d;
  const profitPerMonth = revenuePerMonth * margin;
  const annualRevenue = Math.round(revenuePerMonth * 12);
  const annualProfit = Math.round(profitPerMonth * 12);
  const halfYearRevenue = Math.round(annualRevenue / 2);
  const halfYearProfit = Math.round(annualProfit / 2);
  // LTV per ACQUIRED customer (= per close, one new end-customer):
  //   Profit pro Abschluss × Lifetime-Jahre = d × margin × lifetimeYears
  // This is the Kunde-Profit pro Lead, der dem Kunden über die gesamte
  // Lifetime des akquirierten End-Kunden entsteht.
  const ltvPerCustomer = Math.round(d * margin * lifetimeYears);
  const lifetimeProfit = Math.round(annualProfit * lifetimeYears);
  const lifetimeRevenue = Math.round(annualRevenue * lifetimeYears);
  const isOverridden = override > 0;
  return {
    apptPerMonth,
    closesPerMonth,
    closesPerYear,
    revenuePerMonth,
    profitPerMonth,
    // Backwards-compat: `annual` and `halfYear` now refer to PROFIT, since
    // that's the meaningful figure for customer ROI. Revenue is exposed
    // separately as `annualRevenue` / `halfYearRevenue`.
    annual: annualProfit,
    halfYear: halfYearProfit,
    annualRevenue,
    halfYearRevenue,
    annualProfit,
    halfYearProfit,
    lifetimeYears,
    ltvPerCustomer,
    lifetimeProfit,
    lifetimeRevenue,
    margin,
    isOverridden,
  };
};

/**
 * Compute the AGENCY-side revenue for a tier: what we (BonusClub) earn from
 * this customer per year, plus the customer's ROI (their gross revenue divided
 * by what they pay us). Used purely internally — never published to a customer.
 *
 *   ourMonthly = monthlyPrice + max(0, apptPerMonth - freie Termine) × appointmentValue
 *   ourAnnual  = 12 × ourMonthly  (setup fee added on first year only, returned separately)
 *   customerRoi = lifetimeProfit (full LTV der in 1 Jahr akquirierten End-Kunden)
 *                 / ourFirstYearCost (12× Monthly + einmalig Setup)
 *
 * Diese ROI-Definition gibt die ehrliche Sales-Geschichte wieder:
 * Selbst wenn der Kunde uns nach 1 Jahr kündigt, behält er die End-Kunden,
 * die wir ihm gebracht haben — und diese zahlen über die volle End-Kunden-
 * Lifetime weiter. Damit wächst der ROI linear mit `lifetimeYears`.
 */
export const computeAgencyProjection = (
  cfg,
  tierIdx,
  { monthlyPrice = 0, appointmentValue = 0, setupFee = 0 } = {}
) => {
  const cust = computeRevenueProjection(cfg, tierIdx);
  const freeAppointments = Math.max(0, resolveBonusFromAppointment(cfg) - 1);
  const billableApptsPerMonth = Math.max(
    0,
    cust.apptPerMonth - freeAppointments
  );
  const bonusPerMonth = billableApptsPerMonth * (Number(appointmentValue) || 0);
  const ourMonthly = (Number(monthlyPrice) || 0) + bonusPerMonth;
  const ourAnnual = Math.round(ourMonthly * 12);
  const ourAnnualWithSetup = ourAnnual + (Number(setupFee) || 0);
  const ourHalfYear = Math.round(ourAnnual / 2);
  // Cost per Acquisition (CAC): was wir kosten, um EINEN End-Kunden zu akquirieren.
  //   CAC = (Jahres-Fees + einmaliger Setup) / Abschlüsse pro Jahr
  // Wenn keine Abschlüsse simuliert sind, CAC = ourAnnualWithSetup (sicherheitshalber).
  const cac =
    cust.closesPerYear > 0
      ? Math.round(ourAnnualWithSetup / cust.closesPerYear)
      : ourAnnualWithSetup;

  // ----- ROI: realistische LTV-vs-CAC-Sicht --------------------------------
  // Schlüssel-Insight (vom Sales-Team gefordert): selbst wenn der Kunde uns
  // nach dem ersten Jahr kündigen würde, behält er die End-Kunden, die WIR
  // ihm in diesem ersten Jahr gebracht haben — und diese End-Kunden zahlen
  // weiter über die volle `lifetimeYears`-Periode. Die ROI-Sicht ist also
  // nicht "wir bezahlen X Jahre, wir bekommen X Jahre zurück" (das hebt
  // sich auf), sondern:
  //   • COST = was 1 Jahr BonusClub kostet (12× ourMonthly + Setup einmalig)
  //   • RETURN = LTV der in diesem 1 Jahr akquirierten End-Kunden
  //              = closesPerYear × (d × margin × lifetimeYears)
  //              = annualProfit × lifetimeYears
  //              = `cust.lifetimeProfit`
  // ROI wächst damit linear mit der End-Kunden-Lifetime — was die Realität
  // viel ehrlicher abbildet (und der Wert, mit dem ein Sales-Team auch
  // gegen "ist das nicht teuer?"-Einwände argumentieren kann).
  //
  // Wir behalten zusätzlich `ourLifetimeCost` (= ourAnnual × lifetime + setup)
  // als rein informative Größe für Dashboards, die bewusst die Steady-State-
  // Sicht zeigen wollen (Kunde bleibt UND BonusClub liefert über die ganze
  // Lifetime weiter).
  const ourFirstYearCost = ourAnnualWithSetup; // 12× Monthly + einmalig Setup
  const ourLifetimeCost = Math.round(
    ourAnnual * cust.lifetimeYears + (Number(setupFee) || 0)
  );
  const customerRoi =
    ourFirstYearCost > 0 ? cust.lifetimeProfit / ourFirstYearCost : 0;

  return {
    ...cust,
    monthlyBase: Number(monthlyPrice) || 0,
    monthlyBonus: bonusPerMonth,
    billableApptsPerMonth,
    ourMonthly,
    ourAnnual,
    ourAnnualWithSetup,
    ourHalfYear,
    cac,
    ourFirstYearCost,
    ourLifetimeCost,
    customerRoi,
  };
};
const computeQualityFactor = (cfg) => {
  const u = Number(cfg.uniquenessScore ?? 5);
  const m = Number(cfg.marketNeedScore ?? 5);
  const b = Number(cfg.blueOceanScore ?? 5);
  return clamp((u + m + b) / 3, 0, 10) / 10;
};
// Mirror of VALUE_PRICING.priceMultiplier in the cloud function.
// Anchors: 1k → 0.95 · 10k → 1.00 · 30k → 1.15 · 50k → 1.65 · 75k → 2.50 · 100k → 3.50
// The deliberate 30k → 50k knick lifts the standard Foundation price to
// ~2.700€ at 50k deal value, then ramps aggressively beyond.
const computeMultiplier = (deal, cfg) => {
  const d = clamp(Number(deal) || 10000, 1000, 100000);
  let m;
  if (d <= 10000) m = 0.95 + ((d - 1000) / 9000) * 0.05;
  else if (d <= 30000) m = 1.0 + ((d - 10000) / 20000) * 0.15;
  else if (d <= 50000) m = 1.15 + ((d - 30000) / 20000) * 0.5;
  else if (d <= 75000) m = 1.65 + ((d - 50000) / 25000) * 0.85;
  else m = 2.5 + ((d - 75000) / 25000) * 1.0;
  const q = computeQualityFactor(cfg);
  if (q < 0.4) m *= 0.97;
  else if (q > 0.7) m *= 1.03;
  return m;
};
const roundToFifty = (v) => Math.round(v / 50) * 50;

// Explicit per-option prices the salesperson set (extracted from the brief at
// intake or entered directly). When present + valid these OVERRIDE the
// standard / value-based prices everywhere — the preview here and the
// generated offer (server mirrors this in generateValueBasedOffer.js).
const sanitizeManualPrices = (cfg) => {
  const arr = Array.isArray(cfg?.tierPriceOverrides) ? cfg.tierPriceOverrides : null;
  if (!arr || arr.length !== 3) return null;
  const prices = arr.map((v) => Math.round(Number(v) || 0));
  if (prices.some((p) => !(p >= 100 && p <= 200000))) return null;
  const sorted = [...prices].sort((a, b) => a - b);
  if (sorted[0] === sorted[1] || sorted[1] === sorted[2]) return null;
  return sorted;
};

// Accepts "1640", "1.640", "1.640 €" and "1,640". Returns a rounded integer
// or null when the field is empty / not a number.
export const parseEuroAmount = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return null;
  let cleaned = s.replace(/€/g, "").replace(/[\s\u00A0]/g, "");
  if (cleaned.includes(",") && cleaned.includes(".")) {
    if (cleaned.lastIndexOf(",") > cleaned.lastIndexOf(".")) {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".");
    } else {
      cleaned = cleaned.replace(/,/g, "");
    }
  } else if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
    cleaned = cleaned.replace(/\./g, "");
  } else if (cleaned.includes(",")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  }
  const n = Number(cleaned);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
};

// Turn the three typed monthly prices into a tierPriceOverrides payload.
// `enginePrices` is what Standard / Value-Based would charge without an
// override. Matching those clears the override so the sliders stay live.
// Prices are stored ascending (Foundation < Plus < Premium), same as the
// generator.
export const commitTierPrices = (draft, enginePrices) => {
  const nums = (Array.isArray(draft) ? draft : []).map(parseEuroAmount);
  if (nums.length !== 3 || nums.some((n) => n == null)) {
    return {
      ok: false,
      overrides: null,
      reordered: false,
      error: "Trag für jedes Paket einen monatlichen Preis ein.",
    };
  }
  if (nums.some((n) => n < 100 || n > 200000)) {
    return {
      ok: false,
      overrides: null,
      reordered: false,
      error: "Jeder Preis liegt zwischen 100 € und 200.000 €.",
    };
  }
  const sorted = [...nums].sort((a, b) => a - b);
  if (sorted[0] === sorted[1] || sorted[1] === sorted[2]) {
    return {
      ok: false,
      overrides: null,
      reordered: false,
      error: "Die drei Preise müssen sich unterscheiden.",
    };
  }
  const sameAsEngine =
    Array.isArray(enginePrices) &&
    enginePrices.length === 3 &&
    sorted.every((n, i) => n === enginePrices[i]);
  return {
    ok: true,
    overrides: sameAsEngine ? null : sorted,
    reordered: sorted.some((n, i) => n !== nums[i]),
    error: "",
  };
};

const formatPriceDraft = (raw, focused) => {
  if (focused) return String(raw ?? "");
  const n = parseEuroAmount(raw);
  if (n == null) return String(raw ?? "");
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(n);
};

const attachAppointmentBonus = (result, cfg, dealValue) => {
  const engineAppointmentValue = computeAppointmentValue(dealValue, cfg.profitMargin);
  const override = resolveAppointmentPriceOverride(cfg);
  const bonusFromAppointment = resolveBonusFromAppointment(cfg);
  return {
    ...result,
    engineAppointmentValue,
    appointmentValue: override == null ? engineAppointmentValue : override,
    appointmentPriceOverridden: override != null,
    bonusFromAppointment,
    freeAppointments: Math.max(0, bonusFromAppointment - 1),
  };
};

const computePricing = (cfg) => {
  const enabled = !!cfg.valueBasedPricingEnabled;
  const dealValue = clamp(Number(cfg.revenuePerClose) || 10000, 1000, 100000);
  // Manual price override wins over both standard and value-based pricing.
  const manualPrices = sanitizeManualPrices(cfg);
  if (manualPrices) {
    return attachAppointmentBonus(
      {
        enabled,
        manualOverride: true,
        dealValue,
        multiplier: 1,
        adjustedPrices: manualPrices,
      },
      cfg,
      dealValue
    );
  }
  if (!enabled) {
    return attachAppointmentBonus(
      {
        enabled: false,
        dealValue,
        multiplier: 1,
        adjustedPrices: [...VALUE_BASE_PRICES],
      },
      cfg,
      dealValue
    );
  }
  const m = computeMultiplier(dealValue, cfg);
  return attachAppointmentBonus(
    {
      enabled: true,
      dealValue,
      multiplier: m,
      // If the raw price falls at or below the floor, snap exactly to the floor
      // (otherwise roundToFifty can nudge a result like 1640 → 1650 and the
      // displayed price would always be 10 € above the advertised minimum).
      adjustedPrices: VALUE_BASE_PRICES.map((p, i) => {
        const raw = p * m;
        if (raw <= VALUE_MIN_PRICES[i]) return VALUE_MIN_PRICES[i];
        return Math.max(VALUE_MIN_PRICES[i], roundToFifty(raw));
      }),
    },
    cfg,
    dealValue
  );
};

const formatEUR = (n) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);

const SCORE_FIELDS = [
  {
    id: "blueOceanScore",
    label: "Ist die Branche des Zielkunden ein \"Blue Ocean\"?",
    hint: "0 = Hart umkämpft + Mini-Markt   |   10 = Zero Konkurrenz + Großer Markt",
  },
  {
    id: "uniquenessScore",
    label: "Wie einzigartig ist der Service oder das Produkt?",
    hint: "0 = Tausende gleiche Anbieter   |   10 = Extrem einzigartig",
  },
  {
    id: "marketNeedScore",
    label: "Wie sehr braucht der Markt das Produkt?",
    hint: "0 = Nicht wirklich   |   10 = Absoluter \"No-Brainer\"",
  },
  {
    id: "onlinePresenceScore",
    label: "Wie stark ist der Online-Auftritt / Social Proof / Brand?",
    hint: "0 = Kaum sichtbar   |   10 = Extrem hochwertig & stark",
  },
  {
    id: "coldOutreachFitScore",
    label: "Wie etabliert ist Cold Outreach in dieser Branche?",
    hint: "0 = Extrem schwer / stark reguliert / nur Empfehlung   |   10 = Absoluter Standard (B2B-SaaS, Agenturen, Recruiting)",
  },
];

const Field = ({ label, required, children, hint, filled }) => (
  <div className={`vbo-cfg__field ${filled ? "vbo-cfg__field--filled" : ""}`}>
    <label className="vbo-cfg__label">
      {label}
      {required && <span className="vbo-cfg__req">*</span>}
      {filled && (
        <span
          className="vbo-cfg__filled-dot"
          title="Wurde von BonusClub Intelligence aus Deinem Brain-Dump extrahiert"
        />
      )}
    </label>
    {hint && <div className="vbo-cfg__hint">{hint}</div>}
    {children}
  </div>
);

const Score = ({ value, onChange }) => (
  <div className="vbo-cfg__score">
    {Array.from({ length: 11 }).map((_, i) => (
      <button
        key={i}
        type="button"
        className={`vbo-cfg__score-pill ${value === i ? "is-active" : ""}`}
        onClick={() => onChange(i)}
      >
        {i}
      </button>
    ))}
  </div>
);

const Configurator = ({
  config,
  onChange,
  baseSets = [],
  baseSetId,
  onBaseSetChange,
  onGenerate,
  isGenerating,
  errors = {},
  filledFields = [],
  onBackToIntake = null,
  onResearch = null,
  isResearching = false,
  scoreReasoning = {},
  researchInfo = null,
  termsLibrary = [],
}) => {
  const set = (key, val) => onChange({ ...config, [key]: val });
  const filled = (key) => filledFields.includes(key);

  const enginePrices = computePricing({
    ...config,
    tierPriceOverrides: null,
  }).adjustedPrices;
  const priceKey = computePricing(config).adjustedPrices.join("|");
  const [priceDraft, setPriceDraft] = useState(() => priceKey.split("|"));
  const [priceFocus, setPriceFocus] = useState(null);
  const [priceError, setPriceError] = useState("");
  const draftRef = useRef(priceDraft);
  const editingPriceRef = useRef(false);

  const engineAppointmentValue = computeAppointmentValue(
    config.revenuePerClose,
    config.profitMargin
  );
  const appliedBonus = computePricing(config);
  const [apptDraft, setApptDraft] = useState(() =>
    String(appliedBonus.appointmentValue)
  );
  const [fromDraft, setFromDraft] = useState(() =>
    String(appliedBonus.bonusFromAppointment)
  );
  const [apptFocus, setApptFocus] = useState(false);
  const [bonusError, setBonusError] = useState("");
  const apptDraftRef = useRef(apptDraft);
  const fromDraftRef = useRef(fromDraft);
  const editingBonusRef = useRef(false);

  useEffect(() => {
    if (editingBonusRef.current) return;
    const price = String(appliedBonus.appointmentValue);
    const from = String(appliedBonus.bonusFromAppointment);
    apptDraftRef.current = price;
    fromDraftRef.current = from;
    setApptDraft(price);
    setFromDraft(from);
  }, [appliedBonus.appointmentValue, appliedBonus.bonusFromAppointment]);

  useEffect(() => {
    if (editingPriceRef.current) return;
    const next = priceKey.split("|");
    draftRef.current = next;
    setPriceDraft(next);
    setPriceError("");
  }, [priceKey]);

  const updatePriceDraft = (index, value) => {
    const cleaned = String(value || "").replace(/[^\d.,\s\u00A0]/g, "");
    const next = [...draftRef.current];
    next[index] = cleaned;
    draftRef.current = next;
    setPriceDraft(next);
    if (priceError) setPriceError("");
  };

  const commitPriceDraft = ({ showErrors }) => {
    const result = commitTierPrices(draftRef.current, enginePrices);
    if (!result.ok) {
      if (showErrors) setPriceError(result.error);
      return false;
    }
    setPriceError(
      result.reordered
        ? "Preise stehen jetzt aufsteigend: Foundation, Plus, Premium."
        : ""
    );
    const prev = Array.isArray(config.tierPriceOverrides)
      ? config.tierPriceOverrides.join("|")
      : "";
    const nextKey = result.overrides ? result.overrides.join("|") : "";
    const nextConfig = { ...config, tierPriceOverrides: result.overrides };
    if (prev !== nextKey) onChange(nextConfig);
    const shown = (result.overrides || enginePrices).map((n) => String(n));
    draftRef.current = shown;
    setPriceDraft(shown);
    return nextConfig;
  };

  const handlePriceFocus = (index, event) => {
    editingPriceRef.current = true;
    setPriceFocus(index);
    const next = [...draftRef.current];
    const n = parseEuroAmount(next[index]);
    if (n != null) next[index] = String(n);
    draftRef.current = next;
    setPriceDraft(next);
    const el = event?.target;
    if (el && typeof el.select === "function") {
      requestAnimationFrame(() => el.select());
    }
  };

  const handlePriceBlur = (event) => {
    const nextEl = event.relatedTarget;
    const staying =
      nextEl && nextEl.dataset && nextEl.dataset.priceInput === "tier";
    setPriceFocus(null);
    if (staying) return;
    editingPriceRef.current = false;
    commitPriceDraft({ showErrors: true });
  };

  const resetPrices = () => {
    editingPriceRef.current = false;
    setPriceFocus(null);
    setPriceError("");
    if (config.tierPriceOverrides) {
      onChange({ ...config, tierPriceOverrides: null });
    } else {
      const shown = enginePrices.map((n) => String(n));
      draftRef.current = shown;
      setPriceDraft(shown);
    }
  };

  const commitBonusDraft = (base, { showErrors }) => {
    const engine = computeAppointmentValue(base.revenuePerClose, base.profitMargin);
    const priceResult = commitAppointmentPrice(apptDraftRef.current, engine);
    const fromResult = commitBonusFrom(fromDraftRef.current);
    if (!priceResult.ok || !fromResult.ok) {
      if (showErrors) setBonusError(priceResult.error || fromResult.error);
      return false;
    }
    setBonusError("");
    const next = {
      ...base,
      appointmentPriceOverride: priceResult.override,
      bonusFromAppointment: fromResult.value,
    };
    const prevPrice = resolveAppointmentPriceOverride(base);
    const prevFrom = resolveBonusFromAppointment(base);
    if (prevPrice !== priceResult.override || prevFrom !== fromResult.value) {
      onChange(next);
    }
    const shownPrice = String(priceResult.override ?? engine);
    apptDraftRef.current = shownPrice;
    fromDraftRef.current = String(fromResult.value);
    setApptDraft(shownPrice);
    setFromDraft(fromDraftRef.current);
    return next;
  };

  const handleBonusFocus = (which) => {
    editingBonusRef.current = true;
    if (which === "price") {
      setApptFocus(true);
      const n = parseEuroAmount(apptDraftRef.current);
      if (n != null) {
        const raw = String(n);
        apptDraftRef.current = raw;
        setApptDraft(raw);
      }
    }
  };

  const handleBonusBlur = (event) => {
    const nextEl = event.relatedTarget;
    const staying = nextEl && nextEl.dataset && nextEl.dataset.bonusInput === "1";
    setApptFocus(false);
    if (staying) return;
    editingBonusRef.current = false;
    commitBonusDraft(config, { showErrors: true });
  };

  const resetBonus = () => {
    editingBonusRef.current = false;
    setApptFocus(false);
    setBonusError("");
    onChange({
      ...config,
      appointmentPriceOverride: null,
      bonusFromAppointment: BONUS_FROM_DEFAULT,
    });
  };

  const handleGenerateClick = () => {
    editingPriceRef.current = false;
    editingBonusRef.current = false;
    const priced = commitPriceDraft({ showErrors: true });
    if (!priced) return;
    const nextConfig = commitBonusDraft(priced, { showErrors: true });
    if (!nextConfig) return;
    onGenerate(nextConfig);
  };

  // ---------- Customer-priority helpers (free text, max PRIORITY_MAX) ----------
  const priorities = (Array.isArray(config.customerPriorities)
    ? config.customerPriorities
    : []
  )
    .map(expandLegacyPriority)
    .filter(Boolean)
    .slice(0, PRIORITY_MAX);

  const setPriorities = (next) => {
    const dedup = [];
    const seen = new Set();
    for (const raw of next) {
      const s = expandLegacyPriority(raw).slice(0, 80).trim();
      if (!s) continue;
      const key = s.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      dedup.push(s);
      if (dedup.length >= PRIORITY_MAX) break;
    }
    set("customerPriorities", dedup);
  };

  const removePriorityAt = (idx) => {
    setPriorities(priorities.filter((_, i) => i !== idx));
  };

  const addPriority = (text) => {
    const s = String(text || "").trim();
    if (!s) return;
    setPriorities([...priorities, s]);
  };

  const replacePriorityAt = (idx, text) => {
    const s = String(text || "").trim();
    const next = [...priorities];
    if (!s) {
      next.splice(idx, 1);
    } else {
      next[idx] = s;
    }
    setPriorities(next);
  };

  const [editingPriorityIdx, setEditingPriorityIdx] = React.useState(null);
  const [priorityDraft, setPriorityDraft] = React.useState("");

  const startEditPriority = (idx) => {
    setEditingPriorityIdx(idx);
    setPriorityDraft(idx < priorities.length ? priorities[idx] : "");
  };
  const cancelEditPriority = () => {
    setEditingPriorityIdx(null);
    setPriorityDraft("");
  };
  const commitEditPriority = () => {
    if (editingPriorityIdx === null) return;
    const idx = editingPriorityIdx;
    if (idx < priorities.length) {
      replacePriorityAt(idx, priorityDraft);
    } else {
      addPriority(priorityDraft);
    }
    cancelEditPriority();
  };

  // Prefill banner: only visible for the first 3 seconds after the configurator
  // mounts (or whenever the count of prefilled fields changes), then fades out.
  const [showPrefillBanner, setShowPrefillBanner] = React.useState(
    filledFields.length > 0
  );
  React.useEffect(() => {
    if (filledFields.length === 0) {
      setShowPrefillBanner(false);
      return undefined;
    }
    setShowPrefillBanner(true);
    const t = setTimeout(() => setShowPrefillBanner(false), 3000);
    return () => clearTimeout(t);
  }, [filledFields.length]);

  return (
    <div className="vbo-cfg">
      {onBackToIntake && (
        <div className="vbo-cfg__topbar">
          <button
            type="button"
            className="vbo-cfg__back"
            onClick={onBackToIntake}
            title="Zurück zum Brain-Dump"
            aria-label="Zurück zum Brain-Dump"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
            <span>Brain-Dump</span>
          </button>
        </div>
      )}

      <div className="vbo-cfg__hero">
        <div className="vbo-cfg__hero-eyebrow">SalesGod Offers</div>
        <h1 className="vbo-cfg__hero-title">Value-Based Offer Konfigurator</h1>
        <p className="vbo-cfg__hero-sub">
          Trag die wichtigsten Infos zum Kunden ein. BonusClub Intelligence
          generiert daraus ein vollständiges, hochwertiges Angebot inkl. Intro,
          Situation, Zielen, Werttext und drei Optionen — auf Basis deines
          hinterlegten Value-Based-Sets.
        </p>
      </div>

      {filledFields.length > 0 && showPrefillBanner && (
        <div className="vbo-cfg__prefill-banner" role="status">
          <span>
            {filledFields.length} Feld{filledFields.length === 1 ? "" : "er"}{" "}
            wurden aus Deinem Brain-Dump vorausgefüllt — grün markiert. Lücken
            füllst Du selbst.
          </span>
        </div>
      )}

      <section className="vbo-cfg__section">
        <h2 className="vbo-cfg__h2">Unternehmen</h2>
        <div className="vbo-cfg__grid vbo-cfg__grid--2">
          <Field label="Name der Firma" required filled={filled("companyName")}>
            <input
              type="text"
              className={`vbo-cfg__input ${errors.companyName ? "has-error" : ""}`}
              value={config.companyName || ""}
              placeholder="z.B. Acme Solutions GmbH"
              onChange={(e) => set("companyName", e.target.value)}
            />
          </Field>
          <Field
            label="Hauptansprechpartner / Rechnungsempfänger"
            hint="Person, die wir hauptsächlich angesprochen haben — wird in der Begrüßung verwendet. Wird automatisch aus dem Brain-Dump übernommen."
            required
            filled={filled("invoiceRecipient")}
          >
            <input
              type="text"
              className={`vbo-cfg__input ${errors.invoiceRecipient ? "has-error" : ""}`}
              value={config.invoiceRecipient || ""}
              placeholder="z.B. Max Mustermann"
              onChange={(e) => set("invoiceRecipient", e.target.value)}
            />
          </Field>
          <Field label="Website-Link" filled={filled("websiteUrl")}>
            <input
              type="text"
              className="vbo-cfg__input"
              value={config.websiteUrl || ""}
              placeholder="https://acme-solutions.de"
              onChange={(e) => set("websiteUrl", e.target.value)}
            />
          </Field>
          <Field
            label="Geschäftsführer (laut Impressum)"
            hint="Wird per Auto-Recherche aus dem Impressum gezogen. Erscheint als Geschäftsführer-Zeile im Briefkopf — nicht in der Begrüßung."
            filled={filled("legalRepresentative")}
          >
            <input
              type="text"
              className="vbo-cfg__input"
              value={config.legalRepresentative || ""}
              placeholder="z.B. Anna Beispiel"
              onChange={(e) => set("legalRepresentative", e.target.value)}
            />
          </Field>
        </div>
        <Field
          label="Weitere Gesprächspartner (optional)"
          hint="Falls wir mit mehreren Personen gleichzeitig gesprochen haben — kommagetrennt. Werden in der Begrüßung mitgenannt, falls als Empfänger gewählt."
          filled={filled("meetingContacts")}
        >
          <input
            type="text"
            className="vbo-cfg__input"
            value={
              Array.isArray(config.meetingContacts)
                ? config.meetingContacts.join(", ")
                : config.meetingContacts || ""
            }
            placeholder="z.B. Christian Schmidt (Marketingleiter), Lisa Wagner"
            onChange={(e) => {
              const list = e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean);
              set("meetingContacts", list);
            }}
          />
        </Field>

        {/* Addressee selector — only visible when BOTH a meeting contact and
            a Geschäftsführer name are known. With just one of them, there is
            nothing to choose from. The selector drives:
              - which name appears as the single „z.Hd."-Zeile im Briefkopf
              - whom the AI greets in the intro and references in
                "danke für unser Gespräch / Ihr Interesse"-line. */}
        {(config.invoiceRecipient || "").trim() &&
          (config.legalRepresentative || "").trim() && (
            <Field
              label="Empfänger des Angebots"
              hint="An wen ist das Angebot formal adressiert? Diese Person erscheint im Briefkopf und wird vom AI in der Begrüßung angesprochen."
            >
              <div className="vbo-cfg__addressee-toggle">
                <button
                  type="button"
                  className={`vbo-cfg__addressee-option ${
                    (config.addresseeMode || "meeting") === "meeting"
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() => set("addresseeMode", "meeting")}
                  aria-pressed={
                    (config.addresseeMode || "meeting") === "meeting"
                  }
                >
                  <span className="vbo-cfg__addressee-option-label">
                    Hauptansprechpartner
                  </span>
                  <span className="vbo-cfg__addressee-option-name">
                    {config.invoiceRecipient}
                  </span>
                </button>
                <button
                  type="button"
                  className={`vbo-cfg__addressee-option ${
                    config.addresseeMode === "legal" ? "is-active" : ""
                  }`}
                  onClick={() => set("addresseeMode", "legal")}
                  aria-pressed={config.addresseeMode === "legal"}
                >
                  <span className="vbo-cfg__addressee-option-label">
                    Geschäftsführer
                  </span>
                  <span className="vbo-cfg__addressee-option-name">
                    {config.legalRepresentative}
                  </span>
                </button>
              </div>
            </Field>
          )}
        <Field
          label="Anschrift / Impressum"
          hint="Wird per Auto-Recherche aus dem Impressum gezogen — Firmenname, Straße, PLZ + Ort, Land. Erscheint als Briefkopf-Anschrift auf dem Angebot."
          filled={filled("companyAddress")}
        >
          <textarea
            className="vbo-cfg__textarea"
            rows={4}
            value={config.companyAddress || ""}
            placeholder={"Acme Solutions GmbH\nMusterstraße 12\n10115 Berlin\nDeutschland"}
            onChange={(e) => set("companyAddress", e.target.value)}
          />
        </Field>
      </section>

      <section className="vbo-cfg__section vbo-cfg__simulator-section">
        <div className="vbo-cfg__simulator-head">
          <h2 className="vbo-cfg__h2">Verkaufswert &amp; Pricing-Modell</h2>
          <button
            type="button"
            className={`vbo-cfg__sim-toggle ${
              config.valueBasedPricingEnabled ? "is-on" : ""
            }`}
            onClick={() =>
              set(
                "valueBasedPricingEnabled",
                !config.valueBasedPricingEnabled
              )
            }
            aria-pressed={!!config.valueBasedPricingEnabled}
            title="Wenn aktiv, werden die Basispreise (Mindest 1.640 / 2.860 / 5.460 €) je nach Verkaufswert + Qualität angepasst, niemals unter die Mindestpreise."
          >
            <span className="vbo-cfg__sim-toggle-track">
              <span className="vbo-cfg__sim-toggle-thumb" />
            </span>
            <span className="vbo-cfg__sim-toggle-label">
              VBP{" "}
              <strong>
                {config.valueBasedPricingEnabled ? "AN" : "AUS"}
              </strong>
            </span>
          </button>
        </div>

        <div className="vbo-cfg__simulator-controls vbo-cfg__simulator-controls--4">
          <div className="vbo-cfg__sim-slider">
            <div className="vbo-cfg__sim-slider-head">
              <span className="vbo-cfg__sim-slider-label">
                Verkaufswert / Jahr
              </span>
              <span className="vbo-cfg__sim-slider-value">
                {formatEUR(
                  clamp(
                    Number(config.revenuePerClose) || 10000,
                    1000,
                    100000
                  )
                )}
              </span>
            </div>
            <input
              type="range"
              className="vbo-cfg__slider vbo-cfg__sim-slider-input"
              min={1000}
              max={100000}
              step={500}
              value={clamp(
                Number(config.revenuePerClose) || 10000,
                1000,
                100000
              )}
              onChange={(e) =>
                set("revenuePerClose", parseInt(e.target.value, 10))
              }
              title="Was ist ein Abschluss dem Kunden pro Jahr wert? Treibt den Termin-Bonus."
            />
            <div className="vbo-cfg__sim-slider-scale">
              <span>1k</span>
              <span>30k</span>
              <span>100k</span>
            </div>
          </div>

          <div className="vbo-cfg__sim-slider">
            <div className="vbo-cfg__sim-slider-head">
              <span className="vbo-cfg__sim-slider-label">
                Marge Kunde
              </span>
              <span className="vbo-cfg__sim-slider-value">
                {clampMargin(config.profitMargin)} %
              </span>
            </div>
            <input
              type="range"
              className="vbo-cfg__slider vbo-cfg__sim-slider-input"
              min={10}
              max={100}
              step={5}
              value={clampMargin(config.profitMargin)}
              onChange={(e) =>
                set("profitMargin", parseInt(e.target.value, 10))
              }
              title="Gewinnmarge auf KUNDENSEITE — wie viel Prozent Profit der Kunde pro Abschluss macht. Standard 30 % (typische B2B-Service-Marge). Höhere Kunden-Marge = höherer Profit pro Termin auf Kundenseite = höherer Termin-Bonus für uns (12,5 % vom Kunden-Profit)."
            />
            <div className="vbo-cfg__sim-slider-scale">
              <span>10 %</span>
              <span>30 %</span>
              <span>60 %</span>
              <span>100 %</span>
            </div>
          </div>

          <div className="vbo-cfg__sim-slider">
            <div className="vbo-cfg__sim-slider-head">
              <span className="vbo-cfg__sim-slider-label">
                Termine / Monat
              </span>
              <span className="vbo-cfg__sim-slider-value">
                {(() => {
                  const v = clamp(
                    Number(config.appointmentsPerMonth) || 0,
                    0,
                    50
                  );
                  return v === 0 ? "Auto" : `${v}`;
                })()}
              </span>
            </div>
            <input
              type="range"
              className="vbo-cfg__slider vbo-cfg__sim-slider-input"
              min={0}
              max={50}
              step={1}
              value={clamp(
                Number(config.appointmentsPerMonth) || 0,
                0,
                50
              )}
              onChange={(e) =>
                set("appointmentsPerMonth", parseInt(e.target.value, 10))
              }
              title="0 = Tier-Standard (5 / 12 / 25). 1–50 = simulierter Wert für alle drei Tiers."
            />
            <div className="vbo-cfg__sim-slider-scale">
              <span>Auto</span>
              <span>12</span>
              <span>25</span>
              <span>50</span>
            </div>
          </div>

          <div className="vbo-cfg__sim-slider">
            <div className="vbo-cfg__sim-slider-head">
              <span className="vbo-cfg__sim-slider-label">
                Lifetime Kunde
              </span>
              <span className="vbo-cfg__sim-slider-value">
                {clampLifetime(config.customerLifetimeYears)}{" "}
                {clampLifetime(config.customerLifetimeYears) === 1
                  ? "Jahr"
                  : "Jahre"}
              </span>
            </div>
            <input
              type="range"
              className="vbo-cfg__slider vbo-cfg__sim-slider-input"
              min={1}
              max={20}
              step={1}
              value={clampLifetime(config.customerLifetimeYears)}
              onChange={(e) =>
                set("customerLifetimeYears", parseInt(e.target.value, 10))
              }
              title="Durchschnittliche Lifetime eines End-Kunden, den wir akquirieren. Treibt den LTV-zu-CAC-ROI: höherer Lifetime = höherer Kunde-Profit pro Lead = besserer ROI."
            />
            <div className="vbo-cfg__sim-slider-scale">
              <span>1 J.</span>
              <span>5 J.</span>
              <span>10 J.</span>
              <span>20 J.</span>
            </div>
          </div>
        </div>

        {(() => {
          const p = computePricing(config);
          const labels = ["Foundation", "Plus", "Premium"];
          const cvs = computeCustomerValueScore(config);
          const cvsLabel = labelForCustomerValueScore(cvs);
          return (
            <div
              className={`vbo-cfg__pricing-preview ${
                p.enabled ? "is-active" : ""
              }`}
            >
              <div className={`vbo-cfg__cv-score vbo-cfg__cv-score--${cvsLabel.tone}`}>
                <div className="vbo-cfg__cv-score-row">
                  <span className="vbo-cfg__cv-score-label">Wertschaffungs-Level</span>
                  <span className="vbo-cfg__cv-score-value">
                    {cvs.toFixed(1)}
                    <span className="vbo-cfg__cv-score-max">/10</span>
                  </span>
                  <span className="vbo-cfg__cv-score-tag">{cvsLabel.label}</span>
                </div>
                <div className="vbo-cfg__cv-score-bar">
                  <div
                    className="vbo-cfg__cv-score-bar-fill"
                    style={{ width: `${(cvs / 10) * 100}%` }}
                  />
                </div>
                <div className="vbo-cfg__cv-score-hint">
                  Wie viel Wert können wir hier schaffen? · Verkaufswert × Termine × Marge
                </div>
              </div>

              <div className="vbo-cfg__manual-price-flag">
                <span className="vbo-cfg__manual-price-flag-dot" aria-hidden="true" />
                <span className="vbo-cfg__manual-price-flag-text">
                  {p.manualOverride
                    ? "Eigene Paketpreise aktiv. Sie gelten 1:1 im Angebot und überschreiben Standard und Value-Based."
                    : "Monatliche Paketpreise direkt in der Karte ändern, um die Vorgabe zu überschreiben."}
                </span>
                {p.manualOverride && (
                  <button
                    type="button"
                    className="vbo-cfg__price-reset"
                    onClick={resetPrices}
                  >
                    Vorgabe wiederherstellen
                  </button>
                )}
              </div>
              {priceError && (
                <div className="vbo-cfg__price-error" role="alert">
                  {priceError}
                </div>
              )}

              <div className="vbo-cfg__pricing-row">
                {p.adjustedPrices.map((price, i) => {
                  const proj = computeAgencyProjection(config, i, {
                    monthlyPrice: price,
                    appointmentValue: p.appointmentValue,
                  });
                  return (
                    <div className="vbo-cfg__pricing-card" key={labels[i]}>
                      <div className="vbo-cfg__pricing-card-head">
                        <span className="vbo-cfg__pricing-tier">
                          {labels[i]}
                        </span>
                        {proj.isOverridden && (
                          <span className="vbo-cfg__pricing-projection-flag">
                            Slider
                          </span>
                        )}
                      </div>
                      <div className="vbo-cfg__pricing-amount-row">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="vbo-cfg__pricing-input"
                          data-price-input="tier"
                          aria-label={`${labels[i]} Monatspreis`}
                          value={formatPriceDraft(
                            priceDraft[i] ?? String(price),
                            priceFocus === i
                          )}
                          style={{
                            width: `${Math.max(
                              5,
                              String(
                                formatPriceDraft(
                                  priceDraft[i] ?? String(price),
                                  priceFocus === i
                                )
                              ).length + 0.6
                            )}ch`,
                          }}
                          onFocus={(e) => handlePriceFocus(i, e)}
                          onBlur={handlePriceBlur}
                          onChange={(e) => updatePriceDraft(i, e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") e.currentTarget.blur();
                          }}
                        />
                        <span className="vbo-cfg__pricing-suffix">€/Mt</span>
                        {proj.monthlyBonus > 0 && (
                          <span
                            className="vbo-cfg__pricing-delta"
                            title={`Termin-Bonus: ${proj.billableApptsPerMonth} × ${formatEUR(
                              p.appointmentValue
                            )} = ${formatEUR(proj.monthlyBonus)} / Monat`}
                          >
                            + {formatEUR(proj.monthlyBonus)} Bonus
                          </span>
                        )}
                      </div>
                      <div className="vbo-cfg__pricing-meta-line">
                        ~{proj.apptPerMonth} Term./Mt ·{" "}
                        {proj.closesPerMonth
                          .toFixed(1)
                          .replace(".", ",")}{" "}
                        Closes
                      </div>
                      <div className="vbo-cfg__pricing-grid">
                        <div className="vbo-cfg__pricing-grid-cell vbo-cfg__pricing-grid-cell--customer">
                          <span className="vbo-cfg__pricing-grid-label">
                            Kunde Profit (LTV gesamt)
                          </span>
                          <strong>{formatEUR(proj.lifetimeProfit)}</strong>
                          <span className="vbo-cfg__pricing-grid-sub">
                            {(proj.closesPerYear || 0)
                              .toFixed(0)
                              .replace(".", ",")}{" "}
                            Closes/J. × {formatEUR(p.dealValue)} ×{" "}
                            {Math.round(proj.margin * 100)} % ×{" "}
                            {proj.lifetimeYears}{" "}
                            {proj.lifetimeYears === 1 ? "Jahr" : "Jahre"}
                          </span>
                        </div>
                        <div className="vbo-cfg__pricing-grid-cell vbo-cfg__pricing-grid-cell--ours">
                          <span className="vbo-cfg__pricing-grid-label">
                            BonusClub / Monat
                          </span>
                          <strong>{formatEUR(proj.ourMonthly)}</strong>
                          <span className="vbo-cfg__pricing-grid-sub">
                            {formatEUR(proj.monthlyBase)} Basis +{" "}
                            {formatEUR(proj.monthlyBonus)} Bonus
                          </span>
                        </div>
                        <div className="vbo-cfg__pricing-grid-cell vbo-cfg__pricing-grid-cell--ours">
                          <span className="vbo-cfg__pricing-grid-label">
                            BonusClub / Jahr
                          </span>
                          <strong>{formatEUR(proj.ourAnnual)}</strong>
                          <span className="vbo-cfg__pricing-grid-sub">
                            CAC pro Lead {formatEUR(proj.cac)}
                          </span>
                        </div>
                      </div>
                      <div
                        className="vbo-cfg__pricing-roi-row"
                        title={
                          "ROI = LTV der in 1 Jahr akquirierten End-Kunden / 1 Jahr BonusClub-Investition.\n" +
                          "Auch wenn der Kunde nach Jahr 1 kündigt, bleiben die End-Kunden, die wir ihm gebracht haben, und zahlen über die volle Lifetime-Periode weiter.\n\n" +
                          "Setup: einmalig — danach reine Monatskosten."
                        }
                      >
                        ROI Kunde{" "}
                        <strong>
                          {proj.customerRoi >= 100
                            ? Math.round(proj.customerRoi)
                            : proj.customerRoi.toFixed(1).replace(".", ",")}
                          ×
                        </strong>
                        <span className="vbo-cfg__pricing-roi-row-extra">
                          LTV {formatEUR(proj.lifetimeProfit)} ÷ 1 Jahr Invest{" "}
                          {formatEUR(proj.ourFirstYearCost)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {(() => {
                const profitPerAppt = computeCustomerAppointmentProfit(
                  p.dealValue,
                  config.profitMargin
                );
                const customerKeeps = Math.max(0, profitPerAppt - p.appointmentValue);
                const roi =
                  p.appointmentValue > 0
                    ? customerKeeps / p.appointmentValue
                    : 0;
                const includedLabel =
                  p.freeAppointments <= 0
                    ? "Jeder Termin wird berechnet."
                    : p.freeAppointments === 1
                      ? "Der erste Termin bleibt im Basispreis."
                      : `Die ersten ${p.freeAppointments} Termine bleiben im Basispreis.`;
                const bonusCustom =
                  p.appointmentPriceOverridden ||
                  p.bonusFromAppointment !== BONUS_FROM_DEFAULT;
                return (
                  <div className="vbo-cfg__pricing-bonus">
                    <div className="vbo-cfg__pricing-bonus-line">
                      <strong>Bonus:</strong>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="vbo-cfg__bonus-input"
                        data-bonus-input="1"
                        aria-label="Termin-Preis"
                        value={formatPriceDraft(apptDraft, apptFocus)}
                        style={{
                          width: `${Math.max(
                            4,
                            String(formatPriceDraft(apptDraft, apptFocus)).length + 0.8
                          )}ch`,
                        }}
                        onFocus={() => handleBonusFocus("price")}
                        onBlur={handleBonusBlur}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^\d.,\s\u00A0]/g, "");
                          apptDraftRef.current = cleaned;
                          setApptDraft(cleaned);
                          if (bonusError) setBonusError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                      />
                      <span>€ ab dem</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="vbo-cfg__bonus-input vbo-cfg__bonus-input--count"
                        data-bonus-input="1"
                        aria-label="Termin-Bonus ab Termin"
                        value={fromDraft}
                        onFocus={() => handleBonusFocus("from")}
                        onBlur={handleBonusBlur}
                        onChange={(e) => {
                          const cleaned = e.target.value.replace(/[^\d]/g, "").slice(0, 2);
                          fromDraftRef.current = cleaned;
                          setFromDraft(cleaned);
                          if (bonusError) setBonusError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") e.currentTarget.blur();
                        }}
                      />
                      <span>. Termin / Monat</span>
                      {bonusCustom && (
                        <button
                          type="button"
                          className="vbo-cfg__price-reset"
                          onClick={resetBonus}
                        >
                          Vorgabe
                        </button>
                      )}
                    </div>
                    <div className="vbo-cfg__pricing-bonus-line vbo-cfg__pricing-bonus-line--meta">
                      {includedLabel}
                    </div>
                    {bonusError && (
                      <div className="vbo-cfg__price-error" role="alert">
                        {bonusError}
                      </div>
                    )}
                    <div
                      className="vbo-cfg__pricing-bonus-line"
                      title={`${formatEUR(p.dealValue)}/Jahr × 30 % Marge × ⅓ Conversion = ${formatEUR(profitPerAppt)} Profit pro Termin. Kunde behält ${formatEUR(customerKeeps)} nach unserer Gebühr.`}
                    >
                      <strong>No-Brainer:</strong>{" "}
                      {formatEUR(profitPerAppt)} Profit/Termin, Kunde behält{" "}
                      {formatEUR(customerKeeps)} ({roi.toFixed(1)}× ROI)
                    </div>
                    {p.enabled && (
                      <div className="vbo-cfg__pricing-bonus-line vbo-cfg__pricing-bonus-line--meta">
                        <strong>Multiplikator:</strong> ×
                        {p.multiplier.toFixed(2)}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}
      </section>

      <section className="vbo-cfg__section">
        <h2 className="vbo-cfg__h2">Kontext</h2>
        <Field label="Jetzt-Situation des Unternehmens" filled={filled("currentSituation")}>
          <textarea
            className="vbo-cfg__textarea"
            rows={3}
            value={config.currentSituation || ""}
            placeholder="Was läuft aktuell? Welche Pain-Points?"
            onChange={(e) => set("currentSituation", e.target.value)}
          />
        </Field>
        <Field label="Situationsüberblick" filled={filled("situationOverview")}>
          <textarea
            className="vbo-cfg__textarea"
            rows={3}
            value={config.situationOverview || ""}
            placeholder="Stichpunkte / freie Beschreibung der Ausgangslage"
            onChange={(e) => set("situationOverview", e.target.value)}
          />
        </Field>
        <Field label="Ziele der Kampagne" filled={filled("campaignGoals")}>
          <textarea
            className="vbo-cfg__textarea"
            rows={3}
            value={config.campaignGoals || ""}
            placeholder="Was wollen wir erreichen?"
            onChange={(e) => set("campaignGoals", e.target.value)}
          />
        </Field>
        <Field label="Zusätzliche Infos" filled={filled("additionalInfo")}>
          <textarea
            className="vbo-cfg__textarea"
            rows={2}
            value={config.additionalInfo || ""}
            placeholder="Sonstige relevante Details"
            onChange={(e) => set("additionalInfo", e.target.value)}
          />
        </Field>
        <Field label="Bisheriger Verlauf der Gespräche, Protokolle, Notizen" filled={filled("conversationHistory")}>
          <textarea
            className="vbo-cfg__textarea"
            rows={4}
            value={config.conversationHistory || ""}
            placeholder="Call-Notizen, Mail-Verlauf, Zitate ..."
            onChange={(e) => set("conversationHistory", e.target.value)}
          />
        </Field>
      </section>

      <section className="vbo-cfg__section">
        <div className="vbo-cfg__section-head">
          <h2 className="vbo-cfg__h2">Scoring</h2>
          {onResearch && (
            <button
              type="button"
              className="vbo-cfg__research-btn"
              onClick={() => onResearch()}
              disabled={
                isResearching ||
                !config.companyName ||
                !config.companyName.trim()
              }
              title={
                !config.companyName?.trim()
                  ? "Trage erst den Firmennamen oben ein"
                  : "BonusClub Intelligence analysiert Branche und Firma per Web-Recherche und schlägt Scoring-Werte vor"
              }
            >
              {isResearching ? (
                <>
                  <span className="vbo-intake__spinner vbo-intake__spinner--sm" />
                  Recherchiert …
                </>
              ) : (
                <>
                  <span className="vbo-cfg__research-icon">⌕</span>
                  Auto-Recherche
                </>
              )}
            </button>
          )}
        </div>
        {researchInfo && (
          <div className="vbo-cfg__research-summary">
            <div className="vbo-cfg__research-summary-row">
              {researchInfo.industry && (
                <span className="vbo-cfg__research-pill">
                  <span className="vbo-cfg__research-pill-label">Branche</span>
                  <strong>{researchInfo.industry}</strong>
                </span>
              )}
              {researchInfo.coldOutreach?.viability && (
                <span className="vbo-cfg__research-pill">
                  <span className="vbo-cfg__research-pill-label">Cold Outreach</span>
                  <strong>{researchInfo.coldOutreach.viability}</strong>
                </span>
              )}
              {researchInfo.competitive?.intensity && (
                <span className="vbo-cfg__research-pill">
                  <span className="vbo-cfg__research-pill-label">Wettbewerb</span>
                  <strong>{researchInfo.competitive.intensity}</strong>
                </span>
              )}
              {researchInfo.brand?.onlinePresence && (
                <span className="vbo-cfg__research-pill">
                  <span className="vbo-cfg__research-pill-label">Brand</span>
                  <strong>{researchInfo.brand.onlinePresence}</strong>
                </span>
              )}
            </div>
            {researchInfo.summary && (
              <div className="vbo-cfg__research-summary-text">
                {researchInfo.summary}
              </div>
            )}
            {Array.isArray(researchInfo.citations) &&
              researchInfo.citations.length > 0 && (
                <div className="vbo-cfg__research-sources">
                  Quellen:{" "}
                  {researchInfo.citations.slice(0, 5).map((c, i) => (
                    <a
                      key={i}
                      href={c.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="vbo-cfg__research-source"
                    >
                      {c.title || new URL(c.url).hostname}
                    </a>
                  ))}
                </div>
              )}
          </div>
        )}
        {SCORE_FIELDS.map((f) => (
          <Field
            key={f.id}
            label={f.label}
            required
            hint={f.hint}
            filled={filled(f.id)}
          >
            <Score
              value={typeof config[f.id] === "number" ? config[f.id] : 5}
              onChange={(v) => set(f.id, v)}
            />
            {scoreReasoning && scoreReasoning[f.id] && (
              <div className="vbo-cfg__score-reasoning" title={scoreReasoning[f.id]}>
                <span className="vbo-cfg__score-reasoning-icon">✦</span>
                {scoreReasoning[f.id]}
              </div>
            )}
          </Field>
        ))}
        {(() => {
          const score = computeCampaignSuccessScore(config);
          const scoreLabel = labelForCampaignSuccess(score);
          return (
            <div
              className={`vbo-cfg__cv-score vbo-cfg__cv-score--${scoreLabel.tone}`}
              style={{ marginTop: 12 }}
            >
              <div className="vbo-cfg__cv-score-row">
                <span className="vbo-cfg__cv-score-label">Erfolgs-Score</span>
                <span className="vbo-cfg__cv-score-value">
                  {score.toFixed(1)}
                  <span className="vbo-cfg__cv-score-max">/10</span>
                </span>
                <span className="vbo-cfg__cv-score-tag">{scoreLabel.label}</span>
              </div>
              <div className="vbo-cfg__cv-score-bar">
                <div
                  className="vbo-cfg__cv-score-bar-fill"
                  style={{ width: `${(score / 10) * 100}%` }}
                />
              </div>
              <div className="vbo-cfg__cv-score-hint">
                Wie gut sind die Voraussetzungen? · Cold-Outreach-Fit (28 %) ·
                Bedarf (22 %) · Brand (22 %) · Einzigartigkeit · Blue Ocean
              </div>
            </div>
          );
        })()}
      </section>

      <section className="vbo-cfg__section">
        <h2 className="vbo-cfg__h2">Kunden-Spirit</h2>

        <Field
          label="Entscheider-Typ (optional)"
          hint="Wenn Du den Persönlichkeitstyp einschätzen kannst, passt die KI die Tonalität dezent an (~20 %). Ohne Auswahl bleibt der Stil neutral."
          filled={filled("decisionMakerType")}
        >
          <div className="vbo-cfg__disc-grid">
            <button
              type="button"
              className={`vbo-cfg__disc-option vbo-cfg__disc-option--neutral ${
                !config.decisionMakerType ? "is-active" : ""
              }`}
              onClick={() => set("decisionMakerType", null)}
            >
              <span className="vbo-cfg__disc-short">—</span>
              <div>
                <div className="vbo-cfg__disc-title">Neutral</div>
                <div className="vbo-cfg__disc-desc">Standard-Tonalität</div>
              </div>
            </button>
            {DECISION_MAKER_TYPES.map((t) => (
              <button
                type="button"
                key={t.id}
                className={`vbo-cfg__disc-option ${
                  config.decisionMakerType === t.id ? "is-active" : ""
                }`}
                onClick={() => set("decisionMakerType", t.id)}
                style={{ "--disc-accent": t.accent }}
              >
                <span className="vbo-cfg__disc-short">{t.short}</span>
                <div>
                  <div className="vbo-cfg__disc-title">{t.title}</div>
                  <div className="vbo-cfg__disc-desc">{t.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </Field>

        <Field
          label="Anrede im Angebot"
          hint="Bestimmt die Tonalität — formell (Sie) oder vertraut (Du)."
          filled={filled("addressForm")}
        >
          <div className="vbo-cfg__address-toggle">
            {[
              { id: "Sie", label: "Sie", desc: "Formell · B2B-Standard" },
              { id: "Du", label: "Du", desc: "Vertraut · Startup/Founder" },
            ].map((o) => (
              <button
                type="button"
                key={o.id}
                className={`vbo-cfg__address-option ${
                  (config.addressForm || "Sie") === o.id ? "is-active" : ""
                }`}
                onClick={() => set("addressForm", o.id)}
              >
                <span className="vbo-cfg__address-option-label">{o.label}</span>
                <span className="vbo-cfg__address-option-desc">{o.desc}</span>
              </button>
            ))}
          </div>
        </Field>

        <Field label={'Wie ist der „Spirit" des Unternehmens?'} required filled={filled("spirit")}>
          <div className="vbo-cfg__choice-grid">
            {SPIRIT_OPTIONS.map((o) => (
              <button
                type="button"
                key={o.id}
                className={`vbo-cfg__choice ${
                  config.spirit === o.id ? "is-active" : ""
                }`}
                onClick={() => set("spirit", o.id)}
              >
                <span className="vbo-cfg__choice-letter">{o.id}</span>
                <div>
                  <div className="vbo-cfg__choice-title">{o.title}</div>
                  <div className="vbo-cfg__choice-desc">{o.desc}</div>
                </div>
              </button>
            ))}
          </div>
        </Field>
      </section>

      <section className="vbo-cfg__section">
        <h2 className="vbo-cfg__h2">Was ist dem Kunden besonders wichtig?</h2>
        <p className="vbo-cfg__hint" style={{ marginBottom: 14 }}>
          Bis zu {PRIORITY_MAX} Punkte. Aus dem Brain-Dump werden passende
          Punkte automatisch erkannt — über das <strong>+</strong> kannst Du
          eigene ergänzen.
        </p>
        <div className="vbo-cfg__priority-grid vbo-cfg__priority-grid--dynamic">
          {Array.from({ length: PRIORITY_MAX }).map((_, idx) => {
            const item = idx < priorities.length ? priorities[idx] : null;
            const isEditing = editingPriorityIdx === idx;
            const isFromAi =
              item && filledFields.includes("customerPriorities");

            if (isEditing) {
              const usedKeys = new Set(
                priorities.map((p, i) =>
                  i === idx ? null : p.toLowerCase().trim()
                )
              );
              const draftLower = priorityDraft.toLowerCase().trim();
              const suggestions = PRIORITY_PRESETS.filter((p) => {
                const k = p.toLowerCase().trim();
                if (usedKeys.has(k)) return false;
                if (!draftLower) return true;
                return k.includes(draftLower);
              }).slice(0, 12);
              const pickPreset = (preset) => {
                if (idx < priorities.length) {
                  replacePriorityAt(idx, preset);
                } else {
                  addPriority(preset);
                }
                setEditingPriorityIdx(null);
                setPriorityDraft("");
              };
              return (
                <div
                  key={idx}
                  className="vbo-cfg__priority vbo-cfg__priority--editing vbo-cfg__priority--picker"
                >
                  <div className="vbo-cfg__priority-picker">
                    <div className="vbo-cfg__priority-picker-input-row">
                      <span className="vbo-cfg__priority-picker-icon" aria-hidden="true">
                        ✎
                      </span>
                      <input
                        type="text"
                        className="vbo-cfg__priority-input"
                        autoFocus
                        maxLength={80}
                        placeholder="Eigenen Punkt eingeben — oder unten wählen"
                        value={priorityDraft}
                        onChange={(e) => setPriorityDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            commitEditPriority();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelEditPriority();
                          }
                        }}
                      />
                      {priorityDraft.trim() && (
                        <button
                          type="button"
                          className="vbo-cfg__priority-picker-confirm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={commitEditPriority}
                          title="Übernehmen (Enter)"
                          aria-label="Übernehmen"
                        >
                          ✓
                        </button>
                      )}
                      <button
                        type="button"
                        className="vbo-cfg__priority-picker-cancel"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={cancelEditPriority}
                        title="Abbrechen (Esc)"
                        aria-label="Abbrechen"
                      >
                        ×
                      </button>
                    </div>
                    {suggestions.length > 0 && (
                      <>
                        <div className="vbo-cfg__priority-picker-label">
                          {draftLower
                            ? "Passende Vorschläge"
                            : "Aus Vorschlägen wählen"}
                        </div>
                        <div className="vbo-cfg__priority-picker-chips">
                          {suggestions.map((s) => (
                            <button
                              type="button"
                              key={s}
                              className="vbo-cfg__priority-picker-chip"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => pickPreset(s)}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            }

            if (item) {
              return (
                <div
                  key={idx}
                  className={`vbo-cfg__priority vbo-cfg__priority--filled ${
                    isFromAi ? "vbo-cfg__priority--ai" : ""
                  }`}
                >
                  <button
                    type="button"
                    className="vbo-cfg__priority-text"
                    onClick={() => startEditPriority(idx)}
                    title="Bearbeiten"
                  >
                    {isFromAi && (
                      <span
                        className="vbo-cfg__priority-ai-dot"
                        title="Aus dem Brain-Dump erkannt"
                      />
                    )}
                    <span className="vbo-cfg__priority-label">{item}</span>
                  </button>
                  <button
                    type="button"
                    className="vbo-cfg__priority-remove"
                    onClick={() => removePriorityAt(idx)}
                    title="Entfernen"
                    aria-label="Entfernen"
                  >
                    ×
                  </button>
                </div>
              );
            }

            return (
              <button
                type="button"
                key={idx}
                className="vbo-cfg__priority vbo-cfg__priority--placeholder"
                onClick={() => startEditPriority(idx)}
                title="Eigenen Punkt hinzufügen"
              >
                <span className="vbo-cfg__priority-plus">+</span>
                <span className="vbo-cfg__priority-placeholder-label">
                  Eigenen Punkt hinzufügen
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="vbo-cfg__section">
        <h2 className="vbo-cfg__h2">Setup-Pakete</h2>
        <p className="vbo-cfg__hint" style={{ marginBottom: 14 }}>
          Wähle, welche Setup-Pakete dem Kunden im Angebot zur Auswahl stehen
          sollen. Standard ist immer empfehlenswert. Premium nur aktivieren,
          wenn der Kunde wirklich High-End-Onboarding wert ist (z.B. hoher
          Deal-Wert, starke Brand, klares Investment-Mindset).
        </p>
        <div className="vbo-cfg__setup-grid">
          {SETUP_PACKAGES.map((pkg) => {
            const enabled = (
              Array.isArray(config.setupPackages) ? config.setupPackages : []
            ).includes(pkg.id);
            const toggle = () => {
              const cur = Array.isArray(config.setupPackages)
                ? config.setupPackages
                : [];
              let next;
              if (enabled) {
                // Don't allow disabling the last enabled package — at least
                // one must always be selectable in the offer.
                if (cur.length <= 1) return;
                next = cur.filter((id) => id !== pkg.id);
              } else {
                next = [...cur, pkg.id];
              }
              // Keep canonical order from SETUP_PACKAGE_IDS.
              next = SETUP_PACKAGE_IDS.filter((id) => next.includes(id));
              set("setupPackages", next);
            };
            return (
              <button
                type="button"
                key={pkg.id}
                className={`vbo-cfg__setup-card ${
                  enabled ? "is-active" : ""
                }`}
                onClick={toggle}
                aria-pressed={enabled}
              >
                <div className="vbo-cfg__setup-head">
                  <span className="vbo-cfg__setup-check" aria-hidden="true">
                    {enabled ? "✓" : ""}
                  </span>
                  <span className="vbo-cfg__setup-title">{pkg.title}</span>
                  <span className="vbo-cfg__setup-price">
                    {pkg.price === 0
                      ? "kostenlos"
                      : `${formatEUR(pkg.price)} einmalig`}
                  </span>
                </div>
                <p className="vbo-cfg__setup-desc">{pkg.description}</p>
                <ul className="vbo-cfg__setup-bullets">
                  {pkg.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      </section>

      <section className="vbo-cfg__section">
        <h2 className="vbo-cfg__h2">Gewähltes Paket (Vorauswahl)</h2>
        <p className="vbo-cfg__hint" style={{ marginBottom: 14 }}>
          Optional: Wenn schon feststeht, welches Paket der Kunde nehmen will,
          wähle es hier vor. Im generierten Angebot wird dieses Paket dann mit
          einem Häkchen markiert und die übrigen dezent ausgegraut. Nichts
          auswählen = alle drei Pakete gleichwertig anzeigen.
        </p>
        {(() => {
          const tierPricing = computePricing(config);
          return (
            <div className="vbo-cfg__setup-grid">
              {OFFER_TIERS.map((tier, idx) => {
                const active = Number(config.selectedTierIndex) === idx;
                const price = tierPricing.adjustedPrices[idx];
                const toggle = () =>
                  set("selectedTierIndex", active ? null : idx);
                return (
                  <button
                    type="button"
                    key={tier.id}
                    className={`vbo-cfg__setup-card ${active ? "is-active" : ""}`}
                    onClick={toggle}
                    aria-pressed={active}
                  >
                    <div className="vbo-cfg__setup-head">
                      <span
                        className="vbo-cfg__setup-check"
                        aria-hidden="true"
                      >
                        {active ? "✓" : ""}
                      </span>
                      <span className="vbo-cfg__setup-title">{tier.title}</span>
                      <span className="vbo-cfg__setup-price">
                        {formatEUR(price)} /Mt
                      </span>
                    </div>
                    <p className="vbo-cfg__setup-desc">{tier.desc}</p>
                  </button>
                );
              })}
            </div>
          );
        })()}
      </section>

      <section className="vbo-cfg__section">
        <div className="vbo-cfg__terms-head">
          <h2 className="vbo-cfg__h2" style={{ margin: 0 }}>
            Terms (Vertragsbedingungen)
          </h2>
          {termsLibrary.length > 0 && (
            <div className="vbo-cfg__terms-bulk">
              <span className="vbo-cfg__terms-counter">
                {(Array.isArray(config.termIds) ? config.termIds : []).length}/
                {termsLibrary.length} ausgewählt
              </span>
              <button
                type="button"
                className="vbo-cfg__terms-bulk-btn"
                onClick={() => set("termIds", termsLibrary.map((t) => t.id))}
                title="Alle Terms aus deiner Bibliothek anhängen"
              >
                Alle auswählen
              </button>
              <button
                type="button"
                className="vbo-cfg__terms-bulk-btn"
                onClick={() =>
                  set(
                    "termIds",
                    termsLibrary
                      .filter((t) => t.enabledByDefault !== false)
                      .map((t) => t.id)
                  )
                }
                title="Nur die als Default markierten Terms auswählen (★ in der Terms-Bibliothek)"
              >
                Auf Defaults
              </button>
              <button
                type="button"
                className="vbo-cfg__terms-bulk-btn vbo-cfg__terms-bulk-btn--ghost"
                onClick={() => set("termIds", [])}
                title="Alle Terms abwählen — das Angebot enthält dann keine Vertragsbedingungen"
              >
                Alle abwählen
              </button>
            </div>
          )}
        </div>
        <p className="vbo-cfg__hint" style={{ marginBottom: 14 }}>
          Wähle aus deiner Terms-Bibliothek, welche Klauseln automatisch unter
          dem Angebot eingefügt werden — direkt oberhalb der
          Repräsentanten-Unterschrift. Du kannst sie nach der Generierung
          jederzeit weiter bearbeiten oder ergänzen.
          {termsLibrary.length === 0 && (
            <>
              {" "}
              <strong>
                Noch keine Terms angelegt — pflege sie unter
                „Offer Creator → Terms".
              </strong>
            </>
          )}
        </p>
        {termsLibrary.length > 0 && (
          <div className="vbo-cfg__terms-list">
            {termsLibrary.map((term) => {
              const sel = Array.isArray(config.termIds)
                ? config.termIds
                : [];
              const enabled = sel.includes(term.id);
              const toggle = () => {
                const cur = sel;
                let next;
                if (enabled) {
                  next = cur.filter((id) => id !== term.id);
                } else {
                  next = [...cur, term.id];
                }
                // Keep canonical order from the library so the offer renders
                // them in the user-defined sequence regardless of click order.
                const orderMap = new Map(
                  termsLibrary.map((t, i) => [t.id, i])
                );
                next.sort(
                  (a, b) =>
                    (orderMap.get(a) ?? 9999) - (orderMap.get(b) ?? 9999)
                );
                set("termIds", next);
              };
              const preview = String(term.html || "")
                .replace(/<[^>]+>/g, " ")
                .replace(/\s+/g, " ")
                .trim();
              return (
                <button
                  type="button"
                  key={term.id}
                  className={`vbo-cfg__term-row ${
                    enabled ? "is-active" : ""
                  }`}
                  onClick={toggle}
                  aria-pressed={enabled}
                >
                  <span
                    className="vbo-cfg__term-check"
                    aria-hidden="true"
                  >
                    {enabled ? "✓" : ""}
                  </span>
                  <span className="vbo-cfg__term-body">
                    <span className="vbo-cfg__term-title">{term.title}</span>
                    {preview && (
                      <span className="vbo-cfg__term-preview">
                        {preview.length > 160
                          ? preview.slice(0, 160) + "…"
                          : preview}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </section>

      <section className="vbo-cfg__section">
        <h2 className="vbo-cfg__h2">Basis-Set</h2>
        <Field
          label="Welches Value-Based Set soll als Basis dienen?"
          hint={'Wird in den OfferCreator-Settings unter „Value Based" verwaltet. Optional — ohne Set entwirft die KI selbst eine Struktur.'}
        >
          <select
            className="vbo-cfg__input"
            value={baseSetId || ""}
            onChange={(e) => onBaseSetChange(e.target.value || null)}
          >
            <option value="">— Kein Basis-Set (KI entwirft selbst) —</option>
            {baseSets.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title || "(untitled)"} ·{" "}
                {(s.options || []).length} Opt.
              </option>
            ))}
          </select>
        </Field>
      </section>

      <div className="vbo-cfg__cta">
        <div
          className={`vbo-cfg__cta-wrap ${
            isGenerating ? "is-generating" : ""
          } ${isResearching && !isGenerating ? "is-waiting-research" : ""}`}
        >
          <div className="vbo-cfg__cta-aurora" aria-hidden="true" />
          <button
            type="button"
            className="vbo-cfg__btn vbo-cfg__btn--primary vbo-cfg__btn--hero"
            onClick={handleGenerateClick}
            disabled={isGenerating || isResearching}
            title={
              isResearching
                ? "Recherche läuft noch (~60 Sek.) — Impressum, GF & Marktdaten werden gerade gezogen."
                : undefined
            }
          >
            <span className="vbo-cfg__btn-shimmer" aria-hidden="true" />
            {isGenerating ? (
              <>
                <span className="vbo-cfg__spinner" />
                <span className="vbo-cfg__btn-label">
                  BonusClub Intelligence generiert …
                </span>
              </>
            ) : isResearching ? (
              <>
                <span className="vbo-cfg__spinner" />
                <span className="vbo-cfg__btn-label">
                  Recherche läuft … (~60 Sek.)
                </span>
              </>
            ) : (
              <>
                <svg
                  className="vbo-cfg__btn-spark vbo-cfg__btn-spark--lg"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 2 13.6 9.2 20.8 10.8 13.6 12.4 12 19.6 10.4 12.4 3.2 10.8 10.4 9.2 12 2Z" />
                </svg>
                <span className="vbo-cfg__btn-label">
                  Value-Based Offer generieren
                </span>
                <svg
                  className="vbo-cfg__btn-spark vbo-cfg__btn-spark--sm"
                  viewBox="0 0 24 24"
                  width="13"
                  height="13"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 4 13 10 19 11 13 12 12 18 11 12 5 11 11 10 12 4Z" />
                </svg>
              </>
            )}
          </button>
          <div className="vbo-cfg__cta-caption">
            <span className="vbo-cfg__cta-caption-dot" />
            {isResearching && !isGenerating
              ? "Web-Recherche läuft — Impressum, Geschäftsführer & Marktdaten werden gezogen."
              : "BonusClub Intelligence · ~60 Sek. · stilkonsistent + preis-kalibriert"}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Configurator;
